import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Search,
  Filter,
  Download,
  RefreshCw,
  Eye,
  Calendar,
  X,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  CheckCircle,
  XCircle,
  AlertCircle,
  DollarSign,
  User,
  FileText,
  FileSpreadsheet,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import { API_BASE } from '../config';

// Types
interface ShiftHistoryData {
  id: string;
  email: string;
  fromPlan: string;
  toPlan: string;
  amount: string;
  status: 'SUCCESS' | 'FAILED' | 'PARTIAL';
  createdAt: string;
  updatedAt: string;
  metadata: {
    originalAmount?: string;
    remainingAmount?: string;
    transferType?: 'FULL' | 'PARTIAL';
    totalLotsTransferred?: number;
    platinumLotsCreated?: number;
    notes?: string;
  };
  formattedAmount: string;
  timeAgo: string;
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface Statistics {
  totalTransfers: number;
  totalAmount: number;
  formattedTotalAmount: string;
  successCount: number;
  failedCount: number;
  partialCount: number;
  successRate: string;
  fromPlanDistribution: Record<string, number>;
  toPlanDistribution: Record<string, number>;
}

interface Filters {
  email?: string;
  fromPlan?: string;
  toPlan?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

const ShiftHistory: React.FC = () => {
  // State
  const [data, setData] = useState<ShiftHistoryData[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [statistics, setStatistics] = useState<Statistics>({
    totalTransfers: 0,
    totalAmount: 0,
    formattedTotalAmount: '$0.00',
    successCount: 0,
    failedCount: 0,
    partialCount: 0,
    successRate: '0%',
    fromPlanDistribution: {},
    toPlanDistribution: {},
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedShift, setSelectedShift] = useState<ShiftHistoryData | null>(null);
  const [exportFormat, setExportFormat] = useState<'csv' | 'excel'>('csv');
  const [includeFiltersInExport, setIncludeFiltersInExport] = useState(true);

  // Filters state
  const [filters, setFilters] = useState<Filters>({
    page: 1,
    limit: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  // Available options
  const planOptions = ['BASIC', 'CLASSIC', 'NORMAL', 'PLATINUM', 'CLASSIC_PLUS'];
  const statusOptions = ['SUCCESS', 'FAILED', 'PARTIAL'];


  // Helper functions
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: any): string => {
    const num = parseFloat(amount || 0);
    const safeNum = isNaN(num) ? 0 : num;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(safeNum);
  };

  const getStatusColor = (status: string): string => {
    const s = (status || '').toUpperCase().trim();
    switch (s) {
      case 'SUCCESS':
        return 'bg-green-100 text-green-800';
      case 'FAILED':
        return 'bg-red-100 text-red-800';
      case 'PARTIAL':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPlanColor = (plan: string): string => {
    const p = (plan || '').toUpperCase().trim();
    const colors: Record<string, string> = {
      'BASIC': 'bg-blue-100 text-blue-800',
      'CLASSIC': 'bg-purple-100 text-purple-800',
      'NORMAL': 'bg-gray-100 text-gray-800',
      'PLATINUM': 'bg-yellow-100 text-yellow-800',
      'CLASSIC_PLUS': 'bg-indigo-100 text-indigo-800',
    };
    return colors[p] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'SUCCESS':
        return <CheckCircle className="h-4 w-4 mr-1" />;
      case 'FAILED':
        return <XCircle className="h-4 w-4 mr-1" />;
      case 'PARTIAL':
        return <AlertCircle className="h-4 w-4 mr-1" />;
      default:
        return null;
    }
  };

  // Calculate active filters count
  const getActiveFiltersCount = (): number => {
    let count = 0;
    if (filters.email) count++;
    if (filters.fromPlan) count++;
    if (filters.toPlan) count++;
    if (filters.status) count++;
    if (filters.startDate || filters.endDate) count++;
    if (filters.search) count++;
    return count;
  };

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const response = await axios.get(`${API_BASE}/api/admin/shift-history?${params.toString()}`);
      const responseData = response.data;

      if (responseData.status === 'success') {
        setData(responseData.data.history);
        setPagination(responseData.data.pagination);
        setStatistics(responseData.data.statistics);
      } else {
        throw new Error(responseData.message || 'Failed to fetch data');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch shift history');
      console.error('Error fetching shift history:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle filter changes
  const handleFilterChange = (field: keyof Filters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: field !== 'page' ? 1 : value, // Reset to page 1 if not pagination
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      page: 1,
      limit: 20,
      sortBy: 'createdAt',
      sortOrder: 'desc',
    });
  };

  // Handle sort
  const handleSort = (field: string) => {
    setFilters(prev => ({
      ...prev,
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === 'asc' ? 'desc' : 'asc',
    }));
  };

  // Handle export
  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      params.append('format', exportFormat);

      if (includeFiltersInExport) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '' &&
            !['page', 'limit', 'sortBy', 'sortOrder'].includes(key)) {
            params.append(key, value.toString());
          }
        });
      }

      const response = await axios.get(`${API_BASE}/api/admin/shift-history/export?${params.toString()}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().split('T')[0];
      link.href = url;
      link.setAttribute('download', `shift-history-${timestamp}.${exportFormat}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setShowExportModal(false);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Export failed. Please try again.');
    }
  };

  // Handle view details
  const handleViewDetails = async (id: string) => {
    try {
      const response = await axios.get(`${API_BASE}/api/admin/shift-history/${id}`);
      if (response.data.status === 'success') {
        setSelectedShift(response.data.data);
        setShowDetailsModal(true);
      } else {
        throw new Error(response.data.message || 'Failed to fetch details');
      }
    } catch (err) {
      console.error('Failed to fetch shift details:', err);
      alert('Failed to load shift details.');
    }
  };

  // Render loading skeleton
  const renderSkeleton = () => (
    <div className="animate-pulse">
      <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="bg-white shadow rounded-lg p-5">
            <div className="h-8 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        ))}
      </div>
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="h-12 bg-gray-200"></div>
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-16 border-b border-gray-200 p-4">
            <div className="flex space-x-4">
              <div className="h-4 bg-gray-200 rounded w-1/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/4"></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Render error state
  const renderError = () => (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
      <div className="flex">
        <div className="flex-shrink-0">
          <XCircle className="h-5 w-5 text-red-400" />
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-red-800">Error loading data</h3>
          <div className="mt-2 text-sm text-red-700">
            <p>{error}</p>
          </div>
          <div className="mt-4">
            <button
              onClick={fetchData}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-4 md:p-6 pb-20">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">Shift History</h1>
          <p className="text-sm md:text-base text-gray-600 mt-1">Track and manage all plan shift operations</p>
        </div>
        <div className="flex flex-wrap gap-2 w-full md:w-auto">
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex-1 md:flex-none inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setShowExportModal(true)}
            className="flex-1 md:flex-none inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {!loading && !error && (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 md:gap-4 mb-6">
          <div className="bg-white overflow-hidden shadow rounded-lg border border-gray-100">
            <div className="p-3 md:p-5">
              <div className="flex items-center">
                <div className="bg-blue-500 rounded-md p-2 md:p-3 shrink-0">
                  <TrendingUp className="h-5 w-5 md:h-6 md:w-6 text-white" />
                </div>
                <div className="ml-3 md:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-xs font-medium text-gray-500 truncate">Total Transfers</dt>
                    <dd className="text-sm md:text-lg font-semibold text-gray-900">
                      {statistics.totalTransfers.toLocaleString()}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg border border-gray-100">
            <div className="p-3 md:p-5">
              <div className="flex items-center">
                <div className="bg-green-500 rounded-md p-2 md:p-3 shrink-0">
                  <DollarSign className="h-5 w-5 md:h-6 md:w-6 text-white" />
                </div>
                <div className="ml-3 md:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-xs font-medium text-gray-500 truncate">Total Amount</dt>
                    <dd className="text-sm md:text-lg font-semibold text-gray-900">
                      {statistics.formattedTotalAmount}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg border border-gray-100">
            <div className="p-3 md:p-5">
              <div className="flex items-center">
                <div className="bg-emerald-500 rounded-md p-2 md:p-3 shrink-0">
                  <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-white" />
                </div>
                <div className="ml-3 md:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-xs font-medium text-gray-500 truncate">Success Rate</dt>
                    <dd className="text-sm md:text-lg font-semibold text-gray-900">
                      {statistics.successRate}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg border border-gray-100">
            <div className="p-3 md:p-5">
              <div className="flex items-center">
                <div className="bg-emerald-500 rounded-md p-2 md:p-3 shrink-0">
                  <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-white" />
                </div>
                <div className="ml-3 md:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-xs font-medium text-gray-500 truncate">Successful</dt>
                    <dd className="text-sm md:text-lg font-semibold text-gray-900">
                      {statistics.successCount.toLocaleString()}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg border border-gray-100">
            <div className="p-3 md:p-5">
              <div className="flex items-center">
                <div className="bg-red-500 rounded-md p-2 md:p-3 shrink-0">
                  <XCircle className="h-5 w-5 md:h-6 md:w-6 text-white" />
                </div>
                <div className="ml-3 md:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-xs font-medium text-gray-500 truncate">Failed</dt>
                    <dd className="text-sm md:text-lg font-semibold text-gray-900">
                      {statistics.failedCount.toLocaleString()}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg border border-gray-100">
            <div className="p-3 md:p-5">
              <div className="flex items-center">
                <div className="bg-yellow-500 rounded-md p-2 md:p-3 shrink-0">
                  <AlertCircle className="h-5 w-5 md:h-6 md:w-6 text-white" />
                </div>
                <div className="ml-3 md:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-xs font-medium text-gray-500 truncate">Partial</dt>
                    <dd className="text-sm md:text-lg font-semibold text-gray-900">
                      {statistics.partialCount.toLocaleString()}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Filter className="h-5 w-5 text-gray-400" />
            <h3 className="text-lg font-medium text-gray-900">Filters</h3>
            {getActiveFiltersCount() > 0 && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {getActiveFiltersCount()} active
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {getActiveFiltersCount() > 0 && (
              <button
                onClick={handleResetFilters}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <X className="h-3 w-3 mr-1" />
                Clear All
              </button>
            )}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {showFilters ? 'Hide' : 'Show'} Advanced
            </button>
          </div>
        </div>

        {/* Global Search */}
        <div className="mb-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <div className="relative max-w-lg w-full">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Search by email, plan, or notes..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <select
              value={filters.limit}
              onChange={(e) => handleFilterChange('limit', parseInt(e.target.value, 10))}
              className="w-full sm:w-40 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value={20}>20 / page</option>
              <option value={50}>50 / page</option>
              <option value={100}>100 / page</option>
            </select>
          </div>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={filters.email || ''}
                onChange={(e) => handleFilterChange('email', e.target.value)}
                placeholder="Enter email"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            {/* From Plan */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From Plan
              </label>
              <select
                value={filters.fromPlan || ''}
                onChange={(e) => handleFilterChange('fromPlan', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">All Plans</option>
                {planOptions.map((plan) => (
                  <option key={plan} value={plan}>
                    {plan}
                  </option>
                ))}
              </select>
            </div>

            {/* To Plan */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                To Plan
              </label>
              <select
                value={filters.toPlan || ''}
                onChange={(e) => handleFilterChange('toPlan', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">All Plans</option>
                {planOptions.map((plan) => (
                  <option key={plan} value={plan}>
                    {plan}
                  </option>
                ))}
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={filters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">All Status</option>
                {statusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>

            {/* Date Range */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date Range
              </label>
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                <div className="flex-1">
                  <div className="relative">
                    <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <input
                      type="date"
                      value={filters.startDate || ''}
                      onChange={(e) => handleFilterChange('startDate', e.target.value)}
                      className="w-full pl-9 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="relative">
                    <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <input
                      type="date"
                      value={filters.endDate || ''}
                      onChange={(e) => handleFilterChange('endDate', e.target.value)}
                      className="w-full pl-9 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && renderError()}

      {/* Loading State */}
      {loading && !error && renderSkeleton()}

      {/* Data Table */}
      {!loading && !error && (
        <>
          <div className="hidden md:block bg-white shadow rounded-lg overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      onClick={() => handleSort('createdAt')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center">
                        Date
                        {filters.sortBy === 'createdAt' && (
                          filters.sortOrder === 'asc' ?
                            <ChevronUp className="ml-1 h-4 w-4" /> :
                            <ChevronDown className="ml-1 h-4 w-4" />
                        )}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('email')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center">
                        Email
                        {filters.sortBy === 'email' && (
                          filters.sortOrder === 'asc' ?
                            <ChevronUp className="ml-1 h-4 w-4" /> :
                            <ChevronDown className="ml-1 h-4 w-4" />
                        )}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('fromPlan')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center">
                        From Plan
                        {filters.sortBy === 'fromPlan' && (
                          filters.sortOrder === 'asc' ?
                            <ChevronUp className="ml-1 h-4 w-4" /> :
                            <ChevronDown className="ml-1 h-4 w-4" />
                        )}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('toPlan')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center">
                        To Plan
                        {filters.sortBy === 'toPlan' && (
                          filters.sortOrder === 'asc' ?
                            <ChevronUp className="ml-1 h-4 w-4" /> :
                            <ChevronDown className="ml-1 h-4 w-4" />
                        )}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('amount')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center">
                        Amount
                        {filters.sortBy === 'amount' && (
                          filters.sortOrder === 'asc' ?
                            <ChevronUp className="ml-1 h-4 w-4" /> :
                            <ChevronDown className="ml-1 h-4 w-4" />
                        )}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('status')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center">
                        Status
                        {filters.sortBy === 'status' && (
                          filters.sortOrder === 'asc' ?
                            <ChevronUp className="ml-1 h-4 w-4" /> :
                            <ChevronDown className="ml-1 h-4 w-4" />
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-12 text-center">
                        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100">
                          <Download className="h-6 w-6 text-gray-400" />
                        </div>
                        <h3 className="mt-2 text-sm font-semibold text-gray-900">No shift history found</h3>
                        <p className="mt-1 text-sm text-gray-500">
                          Try adjusting your filters or search criteria
                        </p>
                      </td>
                    </tr>
                  ) : (
                    data.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatDate(item.createdAt)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {item.timeAgo}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {item.email}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPlanColor(item.fromPlan)}`}>
                            {item.fromPlan}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPlanColor(item.toPlan)}`}>
                            {item.toPlan}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-gray-900">
                            {item.formattedAmount}
                          </div>
                          {item.metadata.originalAmount && (
                            <div className="text-xs text-gray-500">
                              From: {formatCurrency(item.metadata.originalAmount)}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                            {getStatusIcon(item.status)}
                            {item.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {item.metadata.transferType || 'N/A'}
                          {item.metadata.totalLotsTransferred && (
                            <div className="text-xs text-gray-400">
                              {item.metadata.totalLotsTransferred} lots
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleViewDetails(item.id)}
                            className="text-blue-600 hover:text-blue-900 inline-flex items-center"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Table (Cards) - Hidden on desktop */}
          <div className="md:hidden space-y-4 mb-6">
            {data.length === 0 ? (
              <div className="bg-white p-8 text-center rounded-lg shadow-sm border border-gray-200">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100">
                  <Download className="h-6 w-6 text-gray-400" />
                </div>
                <h3 className="mt-2 text-sm font-semibold text-gray-900">No shift history found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Try adjusting filters or search criteria
                </p>
              </div>
            ) : (
              data.map((item) => (
                <div key={item.id} className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                  <div className="flex justify-between items-start mb-4">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-bold text-gray-900 truncate pr-2">{item.email}</div>
                      <div className="text-[10px] text-gray-400 font-medium mt-0.5">{formatDate(item.createdAt)}</div>
                    </div>
                    <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${getStatusColor(item.status)}`}>
                      {item.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-y-4 gap-x-6 text-[11px] bg-gray-50/50 p-3 rounded-xl border border-gray-50 mb-3">
                    <div className="col-span-1">
                      <span className="block text-[9px] text-gray-400 uppercase font-extrabold mb-1">Shift</span>
                      <div className="flex items-center gap-1.5 overflow-hidden">
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold whitespace-nowrap ${getPlanColor(item.fromPlan)}`}>{item.fromPlan}</span>
                        <ChevronRight className="h-2.5 w-2.5 text-gray-300 shrink-0" />
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold whitespace-nowrap ${getPlanColor(item.toPlan)}`}>{item.toPlan}</span>
                      </div>
                    </div>
                    <div className="col-span-1">
                      <span className="block text-[9px] text-gray-400 uppercase font-extrabold mb-1">Amount</span>
                      <span className="text-gray-900 font-black text-xs">{item.formattedAmount}</span>
                    </div>
                    <div>
                      <span className="block text-[9px] text-gray-400 uppercase font-extrabold mb-1">Type</span>
                      <span className="text-blue-600 font-bold italic truncate block">{item.metadata.transferType || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="block text-[9px] text-gray-400 uppercase font-extrabold mb-1">Quantity</span>
                      <span className="text-gray-700 font-semibold">{item.metadata.totalLotsTransferred || '0'} Lots</span>
                    </div>
                  </div>

                  <div className="flex justify-between items-center text-[10px]">
                    <div className="text-gray-400 font-medium flex items-center gap-2">
                      <span>ID: {item._id?.slice(-6)}</span>
                      <span className="w-1 h-1 bg-gray-200 rounded-full"></span>
                      <span>{item.timeAgo}</span>
                    </div>
                    <button
                      onClick={() => handleViewDetails(item.id)}
                      className="text-blue-600 font-black uppercase text-[9px] tracking-wider flex items-center gap-1 hover:text-blue-800"
                    >
                      View Details
                      <ChevronRight className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handleFilterChange('page', pagination.page - 1)}
                  disabled={!pagination.hasPrevPage}
                  className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${pagination.hasPrevPage
                    ? 'bg-white text-gray-700 hover:bg-gray-50'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                >
                  Previous
                </button>
                <button
                  onClick={() => handleFilterChange('page', pagination.page + 1)}
                  disabled={!pagination.hasNextPage}
                  className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${pagination.hasNextPage
                    ? 'bg-white text-gray-700 hover:bg-gray-50'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span> of{' '}
                    <span className="font-medium">{pagination.total}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => handleFilterChange('page', pagination.page - 1)}
                      disabled={!pagination.hasPrevPage}
                      className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${pagination.hasPrevPage
                        ? 'text-gray-500 hover:bg-gray-50'
                        : 'text-gray-300 cursor-not-allowed'
                        }`}
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronLeft className="h-5 w-5" />
                    </button>

                    {/* Page numbers */}
                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      let pageNum;
                      if (pagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.page <= 3) {
                        pageNum = i + 1;
                      } else if (pagination.page >= pagination.totalPages - 2) {
                        pageNum = pagination.totalPages - 4 + i;
                      } else {
                        pageNum = pagination.page - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handleFilterChange('page', pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${pagination.page === pageNum
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    <button
                      onClick={() => handleFilterChange('page', pagination.page + 1)}
                      disabled={!pagination.hasNextPage}
                      className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${pagination.hasNextPage
                        ? 'text-gray-500 hover:bg-gray-50'
                        : 'text-gray-300 cursor-not-allowed'
                        }`}
                    >
                      <span className="sr-only">Next</span>
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowExportModal(false)} />

            <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
              &#8203;
            </span>

            <div className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                    <Download className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg font-medium leading-6 text-gray-900">
                      Export Shift History
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Choose the format and options for exporting shift history data.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 space-y-4">
                  {/* Format Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Export Format
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => setExportFormat('csv')}
                        className={`flex items-center justify-center px-4 py-3 border rounded-md ${exportFormat === 'csv'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-300 hover:bg-gray-50'
                          }`}
                      >
                        <FileText className={`h-5 w-5 mr-2 ${exportFormat === 'csv' ? 'text-blue-500' : 'text-gray-400'}`} />
                        <span className={exportFormat === 'csv' ? 'text-blue-700 font-medium' : 'text-gray-700'}>
                          CSV File
                        </span>
                      </button>
                      <button
                        onClick={() => setExportFormat('excel')}
                        className={`flex items-center justify-center px-4 py-3 border rounded-md ${exportFormat === 'excel'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-300 hover:bg-gray-50'
                          }`}
                      >
                        <FileSpreadsheet className={`h-5 w-5 mr-2 ${exportFormat === 'excel' ? 'text-blue-500' : 'text-gray-400'}`} />
                        <span className={exportFormat === 'excel' ? 'text-blue-700 font-medium' : 'text-gray-700'}>
                          Excel File
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* Options */}
                  <div>
                    <div className="flex items-center">
                      <input
                        id="include-filters"
                        type="checkbox"
                        checked={includeFiltersInExport}
                        onChange={(e) => setIncludeFiltersInExport(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="include-filters" className="ml-2 block text-sm text-gray-900">
                        Apply current filters to export
                      </label>
                    </div>
                    {includeFiltersInExport && (
                      <div className="mt-2 ml-6 text-sm text-gray-500">
                        <p>Current filters will be included in the exported data.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  onClick={handleExport}
                  className="inline-flex w-full justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export Data
                </button>
                <button
                  onClick={() => setShowExportModal(false)}
                  className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedShift && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowDetailsModal(false)} />

            <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
              &#8203;
            </span>

            <div className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:align-middle">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
                      Shift History Details
                    </h3>

                    <div className="space-y-6">
                      {/* Basic Information */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <div className="flex items-center mb-2">
                            <User className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="text-sm font-medium text-gray-500">Email</span>
                          </div>
                          <p className="text-lg font-semibold text-gray-900">{selectedShift.email}</p>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg">
                          <div className="flex items-center mb-2">
                            <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="text-sm font-medium text-gray-500">Date</span>
                          </div>
                          <p className="text-lg font-semibold text-gray-900">{formatDate(selectedShift.createdAt)}</p>
                        </div>
                      </div>

                      {/* Plan Information */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-500 mb-3">Plan Transfer</h4>
                        <div className="flex items-center justify-center space-x-4">
                          <div className="text-center">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getPlanColor(selectedShift.fromPlan)}`}>
                              {selectedShift.fromPlan}
                            </span>
                            <div className="text-xs text-gray-500 mt-1">From Plan</div>
                          </div>
                          <div className="text-gray-400">
                            <span className="text-xl">→</span>
                          </div>
                          <div className="text-center">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getPlanColor(selectedShift.toPlan)}`}>
                              {selectedShift.toPlan}
                            </span>
                            <div className="text-xs text-gray-500 mt-1">To Plan</div>
                          </div>
                        </div>
                      </div>

                      {/* Amount Information */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-500 mb-3">Amount Details</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <div className="text-xs text-gray-500">Transfer Amount</div>
                            <div className="text-lg font-semibold text-gray-900">
                              {selectedShift.formattedAmount}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500">Transfer Type</div>
                            <div className="text-lg font-semibold text-gray-900">
                              {selectedShift.metadata?.transferType || 'N/A'}
                            </div>
                          </div>
                        </div>
                        {selectedShift.metadata?.originalAmount && (
                          <div className="mt-3 grid grid-cols-2 gap-4">
                            <div>
                              <div className="text-xs text-gray-500">Original Amount</div>
                              <div className="text-sm text-gray-700">
                                {formatCurrency(selectedShift.metadata.originalAmount)}
                              </div>
                            </div>
                            {selectedShift.metadata?.remainingAmount && (
                              <div>
                                <div className="text-xs text-gray-500">Remaining Amount</div>
                                <div className="text-sm text-gray-700">
                                  {formatCurrency(selectedShift.metadata.remainingAmount)}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Status Information */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="text-sm font-medium text-gray-500">Status</h4>
                            <div className="flex items-center mt-1">
                              {getStatusIcon(selectedShift.status)}
                              <span className={`ml-2 text-lg font-semibold ${getStatusColor(selectedShift.status).replace('bg-', 'text-')}`}>
                                {selectedShift.status}
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-xs text-gray-500">Time</div>
                            <div className="text-sm text-gray-700">{selectedShift.timeAgo}</div>
                          </div>
                        </div>
                      </div>

                      {/* Lot Information */}
                      {(selectedShift.metadata?.totalLotsTransferred || selectedShift.metadata?.platinumLotsCreated) && (
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h4 className="text-sm font-medium text-gray-500 mb-3">Lot Information</h4>
                          <div className="grid grid-cols-2 gap-4">
                            {selectedShift.metadata?.totalLotsTransferred && (
                              <div>
                                <div className="text-xs text-gray-500">Lots Transferred</div>
                                <div className="text-lg font-semibold text-gray-900">
                                  {selectedShift.metadata.totalLotsTransferred}
                                </div>
                              </div>
                            )}
                            {selectedShift.metadata?.platinumLotsCreated && (
                              <div>
                                <div className="text-xs text-gray-500">Platinum Lots Created</div>
                                <div className="text-lg font-semibold text-gray-900">
                                  {selectedShift.metadata.platinumLotsCreated}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Notes */}
                      {selectedShift.metadata?.notes && (
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <div className="flex items-center mb-2">
                            <FileText className="h-4 w-4 text-gray-400 mr-2" />
                            <h4 className="text-sm font-medium text-gray-500">Notes</h4>
                          </div>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">
                            {selectedShift.metadata.notes}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftHistory;